import {computed, ref} from 'vue';
import {getNftQtnrTemplateApi} from '@/api/nftApis';
import {QuItem, QuTemplateItem} from '@/models/commonModels';
import {QTNR_SEP} from '@/config/constants';
import {useI18n} from 'vue-i18n';
import {object, string, mixed, boolean, array} from 'yup';
import {getFieldSchema} from '@/script/common/validScript';
import {isValidNum} from '@/utils/phoneNumUtils';
import {getRefCodeValidApi} from '@/api/discoverApis';

export const parseQtnr = (q: any, t?: any) => {
  const name = q.name
  const nameArr = name.split(QTNR_SEP)
  return {
    key: nameArr[1] ? nameArr[1] : nameArr[0],
    name: nameArr[1] && t ? t(`qtnrInput.default.${nameArr[1]}`) : nameArr[0],
    plhd: nameArr[1] && t ? t(`qtnrInput.plhd.${nameArr[1]}`) : q.plhd,
  }
}

export const useNftQtnrTemplate = (options?: { isEdit?: boolean }) => {
  const isEdit = ref(false)
  if (options?.isEdit) isEdit.value = options.isEdit
  const form = ref<any>({})
  const { t } = useI18n()
  const qtnr = ref<{ id: number; qtnrTemplate: Array<QuTemplateItem>}>({
    id: 0,
    qtnrTemplate: []
  })
  const pareseQtnrTemplate = (template: any[]) => {
    return template.map((q: QuItem) => {
      const mappedValue = {
        ...q,
        ...parseQtnr(q, t),
        keyName: q.name,
        required: q.required,
      }
      if (q.default) form.value[mappedValue.id] = q.default
      else form.value[mappedValue.id] = ""
      return mappedValue
    })
  }
  const fetchQtnr = async (nftId: number) => {
    const res = await getNftQtnrTemplateApi(nftId)
    if (res?.qtnrTemplate.length) {
      qtnr.value.qtnrTemplate = pareseQtnrTemplate(res?.qtnrTemplate)
    }
  }
  const phoneRef = ref<any>()
  const addRef = ref()
  const ngList = ref([])
  // 분기(branch)로 같은 타입의 입력이 여러 개 렌더링되면 template ref 가 배열이 되므로
  // 항상 첫 번째가 아니라 검증 대상 필드(id)와 이름이 같은 컴포넌트를 찾아야 한다
  const findCompRef = (compRef: any, id: string) => {
    const target = compRef.value
    if (!target) return undefined
    if (Array.isArray(target)) return target.find((comp: any) => comp?.name === id)
    return target
  }
  const validatePhone = (value: any, id: string) => {
    const comp = findCompRef(phoneRef, id)
    if (comp) {
      return comp.validate()
    } else {
      return isValidNum(value)
    }
  }
  const validateAddress = async (id: string) => {
    const comp = findCompRef(addRef, id)
    if (!comp) return false
    return await comp.validate()
  }
  const validateRefCode = async (value: any) => {
    let validResult = true
    if (value) {
      const {valid}= await getRefCodeValidApi(value, ngList.value)
      validResult = valid
    }
    return validResult
  }
  const validateBranch = (id: string, branch: any) => {
    let valid = true
    if (branch) {
      Object.entries(branch).forEach(([key, value])=> {
        if (form.value[key] !== value) {
          valid = false
        }
      })
    }
    if (!valid) form.value[id] = ''
    return valid
  }
  const schema = computed(() => {
    const schemaShape: any = {}
    const qTemplate = qtnr.value.qtnrTemplate
    for (const qtItem of qTemplate) {
      if (isEdit.value && !qtItem.editable) continue;
      if (validateBranch(qtItem.id, qtItem.branch)) {
        if (qtItem.type === 'TERMS')schemaShape[qtItem.id] = boolean().isTrue()
        else if (qtItem.key === 'email') schemaShape[qtItem.id] = getFieldSchema(qtItem.key)
        else if (qtItem.key === 'phone') schemaShape[qtItem.id] = mixed()
          .test('requiredValid', 'valid.phoneFormat', (value) => validatePhone(value, qtItem.id))
        else if (qtItem.id === 'refCode') schemaShape[qtItem.id] = mixed()
          .test('requiredValid', 'valid.refCode', validateRefCode)
        else if (qtItem.type === 'ADDRESS') schemaShape[qtItem.id] = mixed().test('requiredAddValid', 'valid.required', () => validateAddress(qtItem.id))
        else if (qtItem.key === 'birth') schemaShape[qtItem.id] = string().matches(/^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/, 'valid.birthFormat')
        else if (Array.isArray(qtItem.default)) {
          const min = qtItem.config.min ? qtItem.config.min : 1
          schemaShape[qtItem.id] =
            array()
              .min(min, t('valid.minSelect', { min }))
        }
        else schemaShape[qtItem.id] = string().max(qtItem.maxLength ? qtItem.maxLength : 60, (msg) => t('valid.maxLength', { max: msg.max }))
        if (qtItem.required) {
          schemaShape[qtItem.id] = schemaShape[qtItem.id].required('valid.required')
        }
      }
    }
    return object().shape(schemaShape)
  })

  const getRule = (fieldId: string) => {
    return schema.value?.fields[fieldId]
  }

  const validate = async (target: any) => {
    await schema.value?.validate(target);
  }

  return {
    qtnr,
    fetchQtnr,
    pareseQtnrTemplate,
    schema,
    form,
    validate,
    phoneRef,
    addRef,
    validateBranch,
    ngList,
    getRule,
  }
}

export const isQtnrCplt = (qtnrInfo: any, qtnrTemplate: Array<QuItem>) => {
  if (qtnrTemplate.length) return Object.keys(qtnrInfo).length
  else return true
}
